import { useMemo } from 'react';

import {
  companyStoryBand,
  companyStoryCta,
  companyStoryTeaser,
  companyStoryVisitCta,
} from '@/content/companyStory';

import { type CompanyFact, useCompanyStory } from './useCompanyStory';

/**
 * How much room the host page is lending the band.
 *
 * `full` is the two-column argument /about opens with — a sticky question
 * beside the narrative that answers it. `compact` is the single band the other
 * five pages give it: the same company, stated in two sentences, with the
 * facts set as a rail of cards rather than a row of display figures.
 */
export type CompanyStoryDensity = 'full' | 'compact';

/** A link the band closes on. `null` draws no button at all. */
export type CompanyStoryCta = { label: string; to: string };

export type CompanyStoryBandOptions = {
  density?: CompanyStoryDensity;
  /** Target of the enclosing Section's `aria-labelledby`; unique per page. */
  titleId?: string;
  eyebrow?: string;
  title?: string;
  lede?: string;
  /** `null` suppresses the button; omitted takes the default for the density. */
  cta?: CompanyStoryCta | null;
};

export type CompanyStoryBandModel = {
  titleId: string;
  eyebrow: string;
  title: string;
  lede: string;
  cta: CompanyStoryCta | null;
  /** The narrative the chosen density has room for. */
  narrative: readonly string[];
  facts: readonly CompanyFact[];
  isCompact: boolean;
};

const DEFAULT_TITLE_ID = 'company-story-title';

/**
 * Everything the company story band renders, resolved from the density and
 * whatever the host page chose to override.
 *
 * All of the band's branching is here rather than in the components, so that
 * `CompanyStoryBand` and the two layouts beneath it are structure and nothing
 * else. There are four decisions and every one of them is a decision about
 * content, which is why they are not spread across the JSX:
 *
 * 1. **Which narrative.** The full band has a column for the whole story; the
 *    compact one gets the two-sentence `summary`, which is written for the job
 *    rather than cut down to it (see `useCompanyStory`).
 * 2. **Which heading.** Each host page may pass its own eyebrow, title and
 *    lede — the same prop shape `DeliveryProofBand` already takes — because
 *    five pages carrying one identical paragraph reads as boilerplate and
 *    hands a crawler five duplicate blocks. The facts underneath never vary,
 *    so the framing can differ without the claims differing.
 * 3. **Which call to action.** It is density-dependent and cannot be a
 *    constant: the full band *is* the About page's opening, so "More about
 *    Kabex" there would link a reader to the page they are standing on. The
 *    full band sends them to the office instead, and only the compact band
 *    points at /about — which is the entire point of the band existing.
 * The one decision that is *not* here is how the facts are drawn. The two
 * layouts render different molecules for them — `StoryFact` sets a figure in
 * display type on a rail, `StoryFactCard` sets it as a card — and each layout
 * imports the one it needs. A `variant` flag threaded through here would only
 * have told a component which of two trees to build, which is the branching
 * this hook exists to keep out of them.
 */
export function useCompanyStoryBand({
  density = 'full',
  titleId = DEFAULT_TITLE_ID,
  eyebrow,
  title,
  lede,
  cta,
}: CompanyStoryBandOptions = {}): CompanyStoryBandModel {
  const { paragraphs, summary, facts } = useCompanyStory();

  return useMemo(() => {
    const isCompact = density === 'compact';
    const defaults = isCompact ? companyStoryTeaser : companyStoryBand;
    const defaultCta = isCompact ? companyStoryCta : companyStoryVisitCta;

    return {
      titleId,
      eyebrow: eyebrow ?? defaults.eyebrow,
      title: title ?? defaults.title,
      lede: lede ?? defaults.lede,
      // `?? undefined` would collapse an explicit null into the default, so
      // the suppression has to be tested for rather than coalesced away.
      cta: cta === undefined ? defaultCta : cta,
      narrative: isCompact ? summary : paragraphs,
      facts,
      isCompact,
    };
  }, [density, titleId, eyebrow, title, lede, cta, paragraphs, summary, facts]);
}
